import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { docsApi } from '../api';
import { extractHeadings } from '../markdown/headings';
import { Doc, Search } from './Icons';

const cache = new Map();

function plain(md) {
  return String(md || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/^:{2,3}.*$/gm, ' ')
    .replace(/[#>*_`|[\]()-]/g, ' ')
    .replace(/\s+/g, ' ');
}

function buildIndex(pages) {
  return pages.flatMap((p) => {
    const body = plain(p.content);
    const entries = [{ slug: p.slug, hash: '', title: p.title, section: p.section, text: `${p.description || ''} ${body}`, weight: 3 }];
    extractHeadings(p.content).forEach((h) => entries.push({ slug: p.slug, hash: h.id, title: h.text, section: `${p.section} › ${p.title}`, text: '', weight: 2 }));
    return entries;
  });
}

function score(entry, terms) {
  const title = entry.title.toLowerCase();
  const text = entry.text.toLowerCase();
  let s = 0;
  for (const t of terms) {
    if (title.includes(t)) s += 10 * entry.weight + (title.startsWith(t) ? 5 : 0);
    else if (text.includes(t)) s += 2;
    else return 0;
  }
  return s;
}

function snippet(text, terms) {
  const lower = text.toLowerCase();
  const i = Math.min(...terms.map((t) => lower.indexOf(t)).filter((n) => n >= 0));
  if (!Number.isFinite(i)) return '';
  const start = Math.max(0, i - 40);
  return `${start > 0 ? '…' : ''}${text.slice(start, start + 140).trim()}…`;
}

export default function SearchDialog({ open, onClose, product, version }) {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(null);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const key = `${product}/${version}`;

  useEffect(() => {
    if (!open || !product || !version) return;
    setQuery('');
    setCursor(0);
    setTimeout(() => inputRef.current?.focus(), 0);
    if (cache.has(key)) { setIndex(cache.get(key)); return; }
    docsApi.searchIndex(product, version).then((pages) => {
      const idx = buildIndex(pages);
      cache.set(key, idx);
      setIndex(idx);
    }).catch(() => setIndex([]));
  }, [open, product, version, key]);

  const results = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!index || !terms.length) return [];
    return index
      .map((e) => ({ ...e, score: score(e, terms), snippet: e.text ? snippet(e.text, terms) : '' }))
      .filter((e) => e.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12);
  }, [index, query]);

  useEffect(() => setCursor(0), [query]);

  if (!open) return null;

  const go = (r) => {
    navigate(`/${product}/${version}/${r.slug}${r.hash ? `#${r.hash}` : ''}`);
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && results[cursor]) go(results[cursor]);
  };

  return (
    <div className="docs-search-overlay" onMouseDown={onClose}>
      <div className="docs-search" role="dialog" aria-modal="true" aria-label="Search docs" onMouseDown={(e) => e.stopPropagation()}>
        <div className="docs-search-input">
          <Search size={18} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search the docs…"
            aria-label="Search the docs"
          />
          <kbd>Esc</kbd>
        </div>
        <div className="docs-search-results">
          {!query && <div className="docs-search-empty">Search pages, sections and API fields.</div>}
          {query && index && results.length === 0 && <div className="docs-search-empty">No results for “{query}”.</div>}
          {results.map((r, i) => (
            <button
              type="button"
              key={`${r.slug}#${r.hash}`}
              className={`docs-search-result${i === cursor ? ' is-active' : ''}`}
              onMouseEnter={() => setCursor(i)}
              onClick={() => go(r)}
            >
              <span className="docs-search-icon">{r.hash ? '#' : <Doc size={16} />}</span>
              <span className="docs-search-text">
                <span className="docs-search-title">{r.title}</span>
                <span className="docs-search-meta">{r.section}</span>
                {r.snippet && <span className="docs-search-snippet">{r.snippet}</span>}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
